import React, { useState, useEffect } from 'react';
import { getPlaidItems, getPlaidAccounts, syncPlaidData, disconnectPlaidItem } from '../../services/plaidService';
import PlaidLink from './PlaidLink';
import PlaidLinkModal from './PlaidLinkModal';
import './PlaidAccountsList.css';
import { Building2, CreditCard, TrendingUp, Home, Wallet, RefreshCw, MoreHorizontal } from 'lucide-react';
import Skeleton from '../common/Skeleton/Skeleton';
import { useAuth } from '../../state/AuthContext';
import InlineAlert from '../common/InlineAlert';
import StateBlock from '../common/StateBlock';
import ConfirmDialog from '../common/ConfirmDialog';
import { getUserFacingError } from '../../lib/userFacingErrors';

/**
 * PlaidAccountsList component for displaying connected accounts
 */
function PlaidAccountsList({ onRefresh, needsPlaidConnect, onDismissPlaidConnect, onUpgradeRequired }) {
  const { billingStatus, refreshBilling } = useAuth();
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [openMenuItemId, setOpenMenuItemId] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [plaidLinkError, setPlaidLinkError] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [disconnectItemId, setDisconnectItemId] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [itemsData, accountsData] = await Promise.all([
        getPlaidItems(),
        getPlaidAccounts(),
      ]);
      
      setItems(itemsData);
      setAccounts(accountsData);
    } catch (err) {
      console.error('Error loading Plaid data:', err);
      setError(getUserFacingError(err, 'plaid-load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Close More options dropdown when clicking outside any item-more-wrapper
  useEffect(() => {
    if (!openMenuItemId) return;

    const handleClickOutside = (event) => {
      const withinMoreWrapper = event.target.closest('.item-more-wrapper');
      if (!withinMoreWrapper) {
        setOpenMenuItemId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuItemId]);

  const handleSync = async (itemId = null) => {
    try {
      setSyncing(true);
      await syncPlaidData(itemId);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error syncing:', err);
      setError(getUserFacingError(err, 'plaid-sync'));
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async (itemId) => {
    try {
      setLoading(true);
      await disconnectPlaidItem(itemId);
      setDisconnectItemId(null);
      await loadData();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError(getUserFacingError(err, 'plaid-sync'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (itemId) => {
    setOpenMenuItemId(null);
    setUpdatingItemId(itemId);
  };

  const handleUpdateSuccess = async () => {
    try {
      // Auto-sync after successful update
      await handleSync(updatingItemId);
    } catch (err) {
      console.error('Error syncing after update:', err);
    }
  };

  const handleUpdateExit = (err) => {
    if (err) {
      console.error('Update exited with error:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;

    // If somehow in the future, treat as just now
    if (diffMs <= 0) return 'Just now';

    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) {
      return `${diffSeconds} second${diffSeconds !== 1 ? 's' : ''} ago`;
    }

    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  };

  const getAccountsByItem = (itemId) => {
    return accounts.filter(account => account.plaid_item_id === itemId);
  };

  const getAccountIcon = (type, subtype) => {
    if (type === 'depository') return <Building2 size={20} />;
    if (type === 'credit') return <CreditCard size={20} />;
    if (type === 'investment') return <TrendingUp size={20} />;
    if (type === 'loan') return <Home size={20} />;
    return <Wallet size={20} />;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { text: 'Active', className: 'status-active' },
      error: { text: 'Error', className: 'status-error' },
      needs_reauth: { text: 'Needs Reauth', className: 'status-warning' },
      new_accounts_available: { text: 'New Accounts', className: 'status-warning' },
    };
    
    const badge = badges[status] || badges.active;
    return <span className={`status-badge ${badge.className}`}>{badge.text}</span>;
  };

  const trialPlaidLimitReached = billingStatus?.billing_status === 'trialing' && billingStatus?.plaid_limit_reached;
  const plaidLimitMessage = 'Your free trial includes 1 connected account. Upgrade to add another account.';

  const upgradePrompt = trialPlaidLimitReached ? (
    <div className="plaid-upgrade-card">
      <div>
        <p className="plaid-upgrade-title">Account limit reached</p>
        <p className="plaid-upgrade-text">{plaidLimitMessage}</p>
      </div>
      <button
        type="button"
        className="plaid-upgrade-button"
        onClick={() => onUpgradeRequired?.(plaidLimitMessage)}
      >
        Upgrade
      </button>
    </div>
  ) : null;

  const plaidCallout = needsPlaidConnect ? (
    <div className="plaid-connect-callout" role="status">
      <div>
        <p className="plaid-connect-title">Connect your accounts to finish setup</p>
        <p className="plaid-connect-text">
          Link your bank, credit cards, investments, and loans so we can personalize your guidance.
        </p>
      </div>
      {onDismissPlaidConnect && (
        <button type="button" className="plaid-connect-dismiss" onClick={onDismissPlaidConnect}>
          Dismiss
        </button>
      )}
    </div>
  ) : null;

  const plaidOverlay = plaidLoading ? (
    <div className="plaid-connect-overlay" aria-live="polite">
      <div className="plaid-connect-overlay-card">
        <span className="plaid-connect-spinner" aria-hidden="true"></span>
        <div>
          <p className="plaid-connect-overlay-title">Preparing connection</p>
          <p className="plaid-connect-overlay-text">Opening Plaid Link…</p>
        </div>
      </div>
    </div>
  ) : null;

  if (loading && items.length === 0) {
    return (
      <div className="plaid-accounts-list">
        <Skeleton width="100%" height="56px" variant="rectangular" style={{ borderRadius: 'var(--radius-lg)' }} />
        <div className="accounts-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="account-card-skeleton">
              <div className="skeleton-header">
                <Skeleton variant="circular" width="40px" height="40px" />
                <div className="skeleton-info">
                  <Skeleton width="150px" height="20px" />
                  <Skeleton width="100px" height="16px" />
                </div>
              </div>
              <div className="skeleton-balance">
                <Skeleton width="120px" height="32px" />
              </div>
            </div>
          ))}
        </div>
        {plaidOverlay}
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="plaid-accounts-error">
        <StateBlock
          state="error"
          title={error.title}
          message={error.message}
          actionLabel={error.actionLabel || 'Retry'}
          onAction={loadData}
        />
        {plaidOverlay}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="plaid-accounts-list">
        {plaidCallout}
        {plaidLinkError && (
          <div className="plaid-link-error-slot">
            <InlineAlert
              compact
              title={plaidLinkError.title}
              message={plaidLinkError.message}
            />
          </div>
        )}
        <div className="add-account-card">
          {upgradePrompt || (
            <PlaidLink
              onSuccess={() => { setPlaidLinkError(null); loadData(); refreshBilling(); if (onRefresh) onRefresh(); }}
              onError={setPlaidLinkError}
              onLoadingStateChange={setPlaidLoading}
              onUpgradeRequired={onUpgradeRequired}
            />
          )}
        </div>
        <StateBlock
          state="empty"
          title="No connected accounts yet"
          message="Connect your bank, credit card, and investment accounts to get started."
        />
        {plaidOverlay}
      </div>
    );
  }

  return (
    <div className="plaid-accounts-list">
      {plaidCallout}

      {error && (
        <InlineAlert
          compact
          title={error.title}
          message={error.message}
          actionLabel={error.actionLabel}
          onAction={loadData}
        />
      )}

      {plaidLinkError && (
        <div className="plaid-link-error-slot">
          <InlineAlert
            compact
            title={plaidLinkError.title}
            message={plaidLinkError.message}
          />
        </div>
      )}

      <div className="add-account-card">
        {upgradePrompt || (
          <PlaidLink
            onSuccess={() => { setPlaidLinkError(null); loadData(); refreshBilling(); if (onRefresh) onRefresh(); }}
            onError={setPlaidLinkError}
            onLoadingStateChange={setPlaidLoading}
            onUpgradeRequired={onUpgradeRequired}
          />
        )}
      </div>

      {items.map((item) => {
        const itemAccounts = getAccountsByItem(item.id);
        const primaryAccount = itemAccounts[0] || null;
        
        return (
          <div key={item.id} className="item-card">
            <div className="item-main">
              <div className="item-left">
                <div className="institution-row">
                  <div className="institution-logo">
                    {primaryAccount ? getAccountIcon(primaryAccount.type, primaryAccount.subtype) : <Wallet size={20} />}
                  </div>
                  <div className="institution-text">
                    <div className="institution-name">{item.institution_name || 'Financial Institution'}</div>
                    <div className="item-actions-row">
                      <button
                        onClick={() => handleSync(item.item_id)}
                        disabled={syncing}
                        className="item-refresh"
                        title="Sync this connection"
                      >
                        <RefreshCw size={14} className="refresh-icon" />
                        <span className="refresh-label">Refresh</span>
                      </button>
                      <div className="item-more-wrapper">
                        <button
                          type="button"
                          onClick={() => setOpenMenuItemId(openMenuItemId === item.item_id ? null : item.item_id)}
                          disabled={loading}
                          className="item-more"
                          title="More options"
                        >
                          <MoreHorizontal size={16} className="more-icon" />
                          <span className="more-label">More options</span>
                        </button>
                        {openMenuItemId === item.item_id && (
                          <div className="item-more-menu">
                            <button
                              type="button"
                              className="item-more-menu-item item-more-update"
                              onClick={() => handleUpdate(item.item_id)}
                            >
                              Update account
                            </button>
                            <button
                              type="button"
                              className="item-more-menu-item item-more-remove"
                              onClick={() => {
                                setOpenMenuItemId(null);
                                setDisconnectItemId(item.item_id);
                              }}
                            >
                              Remove account
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="item-right">
                {getStatusBadge(item.status)}
              </div>
            </div>

            <div className="item-meta">
              <span className="meta-item">
                Last synced: {formatDate(item.last_synced_at)} | {itemAccounts.length} account
                {itemAccounts.length !== 1 ? 's' : ''}
              </span>
            </div>

            {item.error_message && item.status !== 'active' && (
              <div className="item-status-message" role="alert">
                {item.error_message}
              </div>
            )}

            {itemAccounts.length > 0 && (
              <div className="item-accounts">
                {itemAccounts.map((account) => (
                  <div key={account.id} className="item-account">
                    <div className="item-account-name">
                      {account.name}
                      {account.mask && (
                        <span className="item-account-mask"> ••{account.mask}</span>
                      )}
                    </div>
                    <div className="item-account-value">
                      {formatCurrency(account.current_balance)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {plaidOverlay}

      <ConfirmDialog
        open={Boolean(disconnectItemId)}
        title="Remove this connection?"
        message="We’ll stop syncing this institution and remove its connected account data from Wealth-Y."
        cancelLabel="Keep connection"
        confirmLabel="Remove connection"
        tone="danger"
        loading={loading}
        onCancel={() => setDisconnectItemId(null)}
        onConfirm={() => handleDisconnect(disconnectItemId)}
      />

      {updatingItemId && (
        <PlaidLinkModal
          itemId={updatingItemId}
          onSuccess={handleUpdateSuccess}
          onExit={handleUpdateExit}
          onClose={() => setUpdatingItemId(null)}
        />
      )}
    </div>
  );
}

export default PlaidAccountsList;
